/**
 * "Turn on notifications" modal. Fires on first launch after signup
 * (and up to 2 re-launches, 7 days apart) for native-app users who
 * haven't decided yet.
 *
 * Web (browser) variant: shows an "install the app" nudge instead of
 * asking for the OS permission (per src/lib/push.ts pushSupported()
 * comment: web push is disabled deliberately).
 *
 * Flow:
 *   1. Mount asks GET /api/me/push-prompt for the current decision.
 *   2. If decision.show=false, render nothing (unmount effectively).
 *   3. If decision.show=true, render the modal.
 *   4. Primary tap:
 *      - native_permission variant: call subscribeToPush() which
 *        fires the OS permission dialog. On success, POST
 *        response="granted". On denial, POST response="system_denied".
 *      - web_install variant: no-op link out (we ship App Store /
 *        Play Store URLs as a follow-up); for now just POST
 *        response="web_install_dismissed" so we don't nag again.
 *   5. "Not now" tap: POST response="soft_dismissed" (or
 *      "web_install_dismissed" on the web variant).
 *
 * Dismissal never re-fetches the decision within the same session:
 * the local `visible` state is the source of truth after mount.
 */
import { useEffect, useState } from "react";
import { isNativeApp } from "../lib/platform";
import { subscribeToPush } from "../lib/push";
import {
  fetchPushPromptDecision,
  recordPushPromptResponse,
  type Decision,
} from "../lib/push-prompt-api";

type Props = {
  venueId: string;
  venueName?: string;
  /**
   * True iff the user is currently signed in. The modal never fires
   * for signed-out visitors (there's nowhere to store the response).
   */
  signedIn: boolean;
};

export function PushPromptModal({ venueId, venueName, signedIn }: Props) {
  const [decision, setDecision] = useState<Decision | null>(null);
  const [visible, setVisible] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!signedIn || !venueId) return;
    let cancelled = false;
    fetchPushPromptDecision({ venueId, isNativeApp: isNativeApp() }).then((d) => {
      if (cancelled || !d) return;
      setDecision(d);
      // This PR renders the native-permission variant only. The
      // web_install variant needs a per-venue App Store / Play Store
      // URL (Fenton to supply for Love Inn), shipped separately.
      // Peer-review 2026-09-28 MEDIUM.
      if (d.show && d.variant === "native_permission") setVisible(true);
    });
    return () => {
      cancelled = true;
    };
  }, [signedIn, venueId]);

  if (!visible || !decision) return null;

  const isNative = decision.variant === "native_permission";

  async function onPrimary() {
    if (!decision) return;
    setBusy(true);
    try {
      if (isNative) {
        try {
          await subscribeToPush(venueId);
          await recordPushPromptResponse({ venueId, response: "granted" });
        } catch (err) {
          // Distinguish OS-level denial (permanent, iOS won't re-show
          // dialog) from other failures (network blip on token
          // register, missing plugin, transient OS error). Recording
          // system_denied for a transient error would permanently
          // silence a user who actually said yes; instead record
          // "granted" so the decision module's granted_but_no_token
          // recovery path kicks in on the next launch. Peer-review
          // 2026-09-28 HIGH.
          const msg = err instanceof Error ? err.message : String(err);
          const isOsDenial = /permission denied/i.test(msg);
          await recordPushPromptResponse({
            venueId,
            response: isOsDenial ? "system_denied" : "granted",
          });
          // eslint-disable-next-line no-console
          console.info(
            "[push-prompt]",
            isOsDenial ? "OS denied" : "transient failure, will retry",
            err,
          );
        }
      } else {
        // Web variant is not rendered in this PR (see decision-module
        // gate + variant filter above). This branch is unreachable
        // for now; kept so a future PR with the App-Store link only
        // needs the primary-tap wiring.
        await recordPushPromptResponse({
          venueId, response: "web_install_dismissed",
        });
      }
    } finally {
      setVisible(false);
      setBusy(false);
    }
  }

  async function onDismiss() {
    if (!decision) return;
    setBusy(true);
    try {
      await recordPushPromptResponse({
        venueId,
        response: isNative ? "soft_dismissed" : "web_install_dismissed",
      });
    } finally {
      setVisible(false);
      setBusy(false);
    }
  }

  const label = venueName || "us";
  const headline = isNative ? `Get notified` : `Get the app`;
  const body = isNative
    ? `You're in. Turn on notifications so you never miss when ${label} announces gigs, drops offers, or confirms your booking.`
    : `For live notifications on gigs, drops and bookings from ${label}, install the app. (App-Store link coming soon.)`;
  const primaryText = isNative ? "Turn on notifications" : "OK";

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "color-mix(in srgb, black 55%, transparent)",
        zIndex: 9999,
        display: "flex",
        alignItems: "flex-end",
        justifyContent: "center",
      }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="push-prompt-title"
    >
      <div
        style={{
          background: "var(--surface, #fff)",
          color: "var(--foreground, #111)",
          borderTopLeftRadius: 18,
          borderTopRightRadius: 18,
          padding: 24,
          width: "100%",
          maxWidth: 480,
          boxShadow: "0 -8px 24px rgba(0,0,0,0.18)",
        }}
      >
        <h2
          id="push-prompt-title"
          style={{ margin: "0 0 8px 0", fontSize: 20, fontWeight: 700 }}
        >
          {headline}
        </h2>
        <p style={{ margin: "0 0 20px 0", fontSize: 15, lineHeight: 1.4 }}>
          {body}
        </p>
        <button
          type="button"
          className="subscribe-btn !font-normal"
          style={{ width: "100%", marginBottom: 8 }}
          disabled={busy}
          onClick={onPrimary}
        >
          {busy ? "…" : primaryText}
        </button>
        <button
          type="button"
          style={{
            background: "transparent",
            border: 0,
            color: "var(--muted, #666)",
            width: "100%",
            padding: 12,
            fontSize: 14,
            cursor: "pointer",
          }}
          disabled={busy}
          onClick={onDismiss}
        >
          Not now
        </button>
      </div>
    </div>
  );
}
